import { useEffect, useState } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { PaperPlane, Envelope, Chat } from '@phosphor-icons/react';

export default function NotificationsPage() {
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedClients, setSelectedClients] = useState([]);
  const [kanal, setKanal] = useState('email');

  useEffect(() => {
    fetchTemplates();
    fetchClients();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/templates');
      setTemplates(response.data);
      
      if (response.data.length === 0) {
        await createDefaultTemplates();
        fetchTemplates();
      }
    } catch (error) {
      toast.error('Greška pri učitavanju šablona');
    } finally {
      setLoading(false);
    }
  };

  const createDefaultTemplates = async () => {
    const defaultTemplates = [
      {
        naziv: 'Podsjetnik za dokumentaciju',
        jezik: 'sr',
        tip: 'email',
        subject: 'Podsjetnik - Dostava dokumentacije',
        sadrzaj: 'Poštovani, podsjećamo Vas da dostavite dokumentaciju do 10-og u mjesecu. Hvala!',
      },
      {
        naziv: 'PDV obaveza',
        jezik: 'sr',
        tip: 'email',
        subject: 'Obavještenje o PDV obavezi',
        sadrzaj: 'Poštovani, obavještavamo Vas o PDV obavezi za tekući mjesec. Molimo Vas da pripremite potrebnu dokumentaciju.',
      },
    ];

    for (const template of defaultTemplates) {
      try {
        await api.post('/templates', template);
      } catch (error) {
        console.error('Error creating template:', error);
      }
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (error) {
      toast.error('Greška pri učitavanju klijenata');
    }
  };

  const handleSendNotifications = async () => {
    if (!selectedTemplate || selectedClients.length === 0) {
      toast.error('Izaberite šablon i klijente');
      return;
    }

    try {
      await api.post('/notifications/send', {
        recipient_ids: selectedClients,
        template_id: selectedTemplate,
        kanal,
      });
      toast.success(`Poslato ${selectedClients.length} obavještenja preko ${kanal}`);
      setIsDialogOpen(false);
      setSelectedTemplate(null);
      setSelectedClients([]);
    } catch (error) {
      toast.error('Greška pri slanju obavještenja');
    }
  };

  const toggleClientSelection = (clientId) => {
    setSelectedClients((prev) =>
      prev.includes(clientId) ? prev.filter((id) => id !== clientId) : [...prev, clientId]
    );
  };

  const selectAllClients = () => {
    setSelectedClients(clients.map((c) => c.id));
  };

  const deselectAllClients = () => {
    setSelectedClients([]);
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-slate-900">Obavještenja i podsjetnici</h3>
          <p className="text-sm text-slate-600 mt-1">Slanje poruka klijentima preko emaila i Vibera</p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 font-medium"
              data-testid="notifications-send-button"
            >
              <PaperPlane size={20} weight="bold" className="mr-2" />
              Pošalji obavještenja
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="notifications-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-medium text-slate-900">Pošalji obavještenja</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Kanal *</Label>
                <Select value={kanal} onValueChange={setKanal}>
                  <SelectTrigger className="bg-white border-slate-300" data-testid="notifications-kanal-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="viber">Viber (Mock)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">Šablon *</Label>
                <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                  <SelectTrigger className="bg-white border-slate-300" data-testid="notifications-template-select">
                    <SelectValue placeholder="Izaberite šablon" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.naziv}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedTemplate && (
                <div className="p-4 bg-slate-50 rounded-md border border-slate-200">
                  <p className="text-xs font-semibold tracking-wider uppercase text-slate-500 mb-2">Pregled šablona</p>
                  {templates.find((t) => t.id === selectedTemplate)?.subject && (
                    <p className="text-sm font-medium text-slate-900 mb-1">
                      Naslov: {templates.find((t) => t.id === selectedTemplate)?.subject}
                    </p>
                  )}
                  <p className="text-sm text-slate-600">
                    {templates.find((t) => t.id === selectedTemplate)?.sadrzaj}
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold tracking-wider uppercase text-slate-500">
                    Klijenti ({selectedClients.length} izabrano)
                  </Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={selectAllClients}
                      data-testid="notifications-select-all"
                    >
                      Izaberi sve
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={deselectAllClients}
                      data-testid="notifications-deselect-all"
                    >
                      Poništi
                    </Button>
                  </div>
                </div>
                <div className="max-h-60 overflow-y-auto border border-slate-200 rounded-md p-3 space-y-2">
                  {clients.map((client) => (
                    <label
                      key={client.id}
                      className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedClients.includes(client.id)}
                        onChange={() => toggleClientSelection(client.id)}
                        className="rounded border-slate-300"
                        data-testid={`notifications-client-${client.id}`}
                      />
                      <span className="text-sm text-slate-700">{client.skraceni_naziv || client.naziv_firme}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="rounded-md px-4 py-2 font-medium"
                  data-testid="notifications-cancel-button"
                >
                  Odustani
                </Button>
                <Button
                  onClick={handleSendNotifications}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 font-medium"
                  data-testid="notifications-submit-button"
                >
                  Pošalji
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-slate-500">Učitavanje...</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          <Card className="p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-50 rounded-md flex items-center justify-center flex-shrink-0">
                <Envelope size={24} weight="duotone" className="text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium text-slate-900 mb-1">Email obavještenja</h4>
                <p className="text-sm text-slate-600 mb-3">
                  Pošaljite automatska obavještenja putem emaila klijentima o PDV obavezama, dokumentaciji i drugim važnim informacijama.
                </p>
                <p className="text-xs text-emerald-600 font-medium">✓ Aktivno</p>
              </div>
            </div>
          </Card>

          <Card className="p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-50 rounded-md flex items-center justify-center flex-shrink-0">
                <Chat size={24} weight="duotone" className="text-purple-600" />
              </div>
              <div>
                <h4 className="font-medium text-slate-900 mb-1">Viber obavještenja</h4>
                <p className="text-sm text-slate-600 mb-3">
                  Viber integracija je trenutno u mock režimu. Priprema sistema za buduće povezivanje sa Viber Business API-jem.
                </p>
                <p className="text-xs text-amber-600 font-medium">⚠ Mock sistem (spreman za integraciju)</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
          <h4 className="font-medium text-slate-900">Dostupni šabloni poruka</h4>
        </div>
        <div className="divide-y divide-slate-200">
          {templates.length === 0 ? (
            <div className="p-6 text-center text-slate-500">Nema šablona</div>
          ) : (
            templates.map((template) => (
              <div key={template.id} className="p-5 hover:bg-slate-50" data-testid={`template-${template.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h5 className="font-medium text-slate-900 mb-1">{template.naziv}</h5>
                    {template.subject && (
                      <p className="text-xs text-slate-500 mb-2">Naslov: {template.subject}</p>
                    )}
                    <p className="text-sm text-slate-600">{template.sadrzaj}</p>
                    <div className="flex gap-3 mt-2 text-xs">
                      <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded">
                        {template.tip}
                      </span>
                      <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded">
                        {template.jezik}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
}
